import { getQueue, mutate, type Ack } from './api'
import { appendNote } from './rebook-message'
import type { Customer, Lead } from './types'
import { addDaysISO, el, shortDate, todayISO, noteDate } from './ui'

// Contact/follow-up (migration 0006): when did we last speak to this customer
// or lead, what was said, and when should it be chased up. One reusable editor
// for both rows, plus the pure helpers the grid / New jobs rows use to surface
// an overdue follow-up without opening the record.
//
// Scope, on purpose: these fields are GENERAL booking/quote follow-up. They
// never read or write payment chasing — chased/unpaid live on cleans rows and
// saving here must not clear or suppress them — and customers.call_notes (the
// sheet's imported column) is a different field this editor never touches.
// Nothing here records WHO made contact: the mailbox is shared.
//
// Lifecycle: the editor owns its draft for as long as it is mounted. Inputs
// are disabled while a save is in flight (typing mid-ACK must not be stamped
// "Saved."), and renderFollowup returns a handle with isDirty()/isSaving() so
// a host screen can defer a background re-render behind an open draft the
// same way the customer screen defers behind an open field editor.

export type FollowupEntity = 'customers' | 'leads'

// The slice of a customer/lead row this editor owns. Customer and Lead both
// satisfy it structurally, so screens pass their record as-is.
export interface FollowupRecord {
  id: number
  version: number
  name?: string | null
  last_contacted_on?: string | null
  contact_note?: string | null
  follow_up_on?: string | null
}

export type FollowupRow = Customer | Lead

// The three stored fields, in form order.
export const FOLLOWUP_FIELDS = ['last_contacted_on', 'contact_note', 'follow_up_on'] as const

// ---- pure helpers (no DOM — used by the row summaries and the tests) ----

// yyyy-mm-dd AND a real calendar date — the same rule the server applies
// (functions/lib/http.ts): a regex alone lets 2026-02-31 through, and a
// follow-up silently saved as 2026-03-03 is a lie about what was typed.
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
function isRealDate(v: string): boolean {
  if (!DATE_RE.test(v)) return false
  const [y, m, d] = v.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

// Due when the date has arrived — today counts, tomorrow does not.
export function isFollowupDue(record: { follow_up_on?: string | null }, today: string = todayISO()): boolean {
  const f = record.follow_up_on
  return typeof f === 'string' && f !== '' && f <= today
}

// One concise line for a grid / New jobs row. Empty when nothing is set — rows
// with no contact history show nothing, not "never contacted" noise.
// A customer's date is "Ask again", the words Where they stand, the Diary and Messages use for the same field (bug
// hunt 8 Oct 2026); an enquiry's stays a quote follow-up.
export function followupSummary(
  record: { follow_up_on?: string | null; last_contacted_on?: string | null },
  today: string = todayISO(),
  who: 'customer' | 'lead' = 'lead',
): string {
  const f = record.follow_up_on
  if (typeof f === 'string' && f !== '') {
    if (who === 'customer') return `${isFollowupDue(record, today) ? 'Due to ask again' : 'Ask again'} ${shortDate(f)}`
    return `${isFollowupDue(record, today) ? 'Follow up due' : 'Follow up'} ${shortDate(f)}`
  }
  const lc = record.last_contacted_on
  if (typeof lc === 'string' && lc !== '') return `Contacted ${shortDate(lc)}`
  return ''
}

// Snooze pushes from TODAY when the current date has arrived (or none is set),
// and from the CURRENT date while it is still in the future — a snooze must
// never pull a future follow-up closer.
export function snoozeTarget(current: string | null | undefined, today: string = todayISO()): string {
  const base = typeof current === 'string' && current > today ? current : today
  return addDaysISO(base, 7)
}

// ---- "not now, ask again on…" ------------------------------------------------
//
// A customer who answers "another month instead" goes quiet until the promised
// date. askAgainLater is the ONE rule — Messages' overdue and coming-up groups,
// the diary's Due this week and the nav badge all read it, so the same customer
// cannot pop up in one list after being quieted in another. It pairs with
// isFollowupDue above: the day the date arrives, askAgainLater turns false and
// isFollowupDue turns true — due again, not quiet any more.

// True only while the promised date is still to come. Today counts as ARRIVED
// (they are due again today), and anything that is not a real calendar date
// quietens nobody — a half-written value must not hide a customer.
export function askAgainLater(record: { follow_up_on?: string | null }, today: string = todayISO()): boolean {
  const f = record.follow_up_on
  return typeof f === 'string' && isRealDate(f) && f > today
}

// The note line one "not now" tap writes, worded the way the owner speaks it:
// "17 Sep: said not now, ask again 17 Oct".
export function notNowNoteLine(dayLabel: string, askLabel: string): string {
  return `${dayLabel}: said not now, ask again ${askLabel}`
}

// The PATCH body for one "not now" tap: the promised date, contacted today,
// and the note line appended (left out when it will not fit under the cap —
// the owner's own note survives, the same rule recordMessage keeps). No
// version here: the caller adds the row's current version, the same split
// followupPatch keeps.
export function notNowPatch(record: FollowupRecord, askOn: string, today: string = todayISO()): Record<string, unknown> {
  const note = appendNote(record.contact_note, notNowNoteLine(noteDate(today), noteDate(askOn)))
  const body: Record<string, unknown> = { follow_up_on: askOn, last_contacted_on: today }
  if (note !== (record.contact_note ?? '')) body.contact_note = note
  return body
}

// ---- draft → PATCH body ------------------------------------------------------

export interface FollowupDraft {
  last_contacted_on: string // '' = clear
  contact_note: string // trimmed on save; '' = clear
  follow_up_on: string
}

// The editor's whole save contract: ONLY the three contact fields, and only
// the ones that actually moved (a clear is a move to null). No other business
// field ever appears in the body, so a save here can never restamp the row's
// prices, statuses or notes. Throws on an impossible date — a browser date
// input cannot produce one, but a scripted set can, and the server would
// reject it anyway; better to say it next to the field.
export function followupPatch(record: FollowupRecord, draft: FollowupDraft): Record<string, unknown> {
  const note = draft.contact_note.trim()
  const values: Record<(typeof FOLLOWUP_FIELDS)[number], string | null> = {
    last_contacted_on: draft.last_contacted_on === '' ? null : draft.last_contacted_on,
    contact_note: note === '' ? null : note,
    follow_up_on: draft.follow_up_on === '' ? null : draft.follow_up_on,
  }
  for (const field of ['last_contacted_on', 'follow_up_on'] as const) {
    if (values[field] !== null && !isRealDate(values[field] as string)) {
      throw new Error(`${field === 'last_contacted_on' ? 'Last contacted' : 'Follow up on'} must be a real date, yyyy-mm-dd`)
    }
  }
  const body: Record<string, unknown> = {}
  for (const field of FOLLOWUP_FIELDS) {
    if ((record[field] ?? null) !== values[field]) body[field] = values[field]
  }
  return body
}

// ---- the editor ---------------------------------------------------------------

// What renderFollowup hands back: the host's window into the draft lifecycle.
export interface FollowupHandle {
  // True while any input differs from the stored row (an impossible date
  // counts — there is something unsaved on screen either way).
  isDirty(): boolean
  // True from the moment Save hands the request to the queue until its ack
  // lands. Inputs are disabled for exactly this window.
  isSaving(): boolean
}

export interface FollowupState {
  dirty: boolean
  saving: boolean
}

// Renders the contact/follow-up editor into `host` (cleared first). `record`
// supplies the current id + version — after each acknowledged save the editor
// re-arms itself from the server's row (only when that row IS this record),
// so a second save in the same editor carries the fresh version instead of
// 409-ing against its own edit. `onSaved` fires with the stored row on every
// acknowledged save; `onStateChange` mirrors dirty/saving for hosts that
// defer their own re-renders behind an open draft.
//
// Save discipline (the app's, kept): nothing autosaves — the quick buttons
// only fill the draft, Save is the only thing that mutates, and it goes
// through the shared version-guarded PATCH queue. Offline parks visibly as
// "queued"; 409/4xx keep the draft on screen with the reason. A save is
// refused while ANY earlier intent to the same row is still unresolved
// (sending, queued, or parked conflict/failed) — overwriting behind a parked
// intent is how someone else's (or your own tab's) edit gets silently lost.
export function renderFollowup(
  host: HTMLElement,
  entity: FollowupEntity,
  record: FollowupRow,
  onSaved?: (row: FollowupRow) => void,
  onStateChange?: (state: FollowupState) => void,
): FollowupHandle {
  let current: FollowupRecord = record
  let saving = false
  host.textContent = ''

  const section = el('section', { class: 'followup', 'aria-label': 'Contact follow-up' })
  const form = el('form', { class: 'followup-form' })
  const fields = el('fieldset', { class: 'booking-fields' })
  const field = (label: string, node: HTMLElement): HTMLElement =>
    el('label', { class: 'booking-field' }, el('span', {}, label), node)

  const last = el('input', { type: 'date' })
  last.value = record.last_contacted_on ?? ''
  const follow = el('input', { type: 'date' })
  follow.value = record.follow_up_on ?? ''
  const note = el('textarea', { rows: '2', maxlength: '2000', placeholder: 'What was said / what to ask next' })
  note.value = record.contact_note ?? ''
  fields.append(
    field('Last contacted', last),
    field(entity === 'customers' ? 'Ask again on' : 'Follow up on', follow),
    field('Contact note', note),
    el(
      'p',
      { class: 'muted field-help' },
      'Booking and quote follow-up. Chasing unpaid cleans is separate and is never cleared or replaced by this.',
    ),
  )

  const status = el('p', { class: 'followup-status', role: 'status', 'aria-live': 'polite' })
  const contacted = el('button', { type: 'button' }, 'Mark contacted today')
  const snooze = el('button', { type: 'button' }, entity === 'customers' ? 'Ask again a week later' : 'Snooze +7 days')
  const clear = el('button', { type: 'button' }, 'Clear dates')
  const save = el('button', { type: 'submit', class: 'btn-primary' }, 'Save contact details')
  const actions = el('div', { class: 'followup-actions' }, contacted, snooze, clear, save)

  const draft = (): FollowupDraft => ({
    last_contacted_on: last.value,
    contact_note: note.value,
    follow_up_on: follow.value,
  })

  const isDirty = (): boolean => {
    try {
      return Object.keys(followupPatch(current, draft())).length > 0
    } catch {
      return true // an impossible date is unsaved work on screen
    }
  }

  let lastState: FollowupState | null = null
  const emitState = (): void => {
    const state = { dirty: isDirty(), saving }
    if (lastState && lastState.dirty === state.dirty && lastState.saving === state.saving) return
    lastState = state
    onStateChange?.(state)
  }

  for (const input of [last, follow, note]) input.addEventListener('input', emitState)

  // Quick buttons fill the DRAFT only — the save stays explicit, so nothing is
  // written by a mis-tap and the user can still back out.
  contacted.addEventListener('click', () => {
    last.value = todayISO()
    say('', '')
    emitState()
  })
  snooze.addEventListener('click', () => {
    follow.value = snoozeTarget(follow.value || current.follow_up_on)
    say('', '')
    emitState()
  })
  clear.addEventListener('click', () => {
    last.value = ''
    follow.value = ''
    say('', '')
    emitState()
  })

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    if (saving) return
    let patch: Record<string, unknown>
    try {
      patch = followupPatch(current, draft())
    } catch (err) {
      say((err as Error).message, 'followup-error')
      return
    }
    if (Object.keys(patch).length === 0) {
      say('Nothing changed.', '')
      emitState()
      return
    }
    const path = `/api/${entity}/${current.id}`
    // ANY unresolved intent to this row — in flight, queued, or parked for
    // review (conflict/failed) — blocks the save. The queue would hold this
    // edit behind it anyway; saying so here is more honest than parking it.
    if (getQueue().some((q) => q.path === path)) {
      say('An earlier change to this row has not finished saving. Check the saving indicator, then save again.', 'followup-queued')
      return
    }
    saving = true
    // Everything a keystroke could land on is locked for the flight: typing
    // during the ACK window must not be stamped "Saved." a moment later.
    fields.disabled = true
    for (const b of [contacted, snooze, clear, save]) b.disabled = true
    emitState()
    const name = current.name ?? `${entity} #${current.id}`
    void mutate({
      label: `Contact follow-up: ${name}`,
      method: 'PATCH',
      path,
      body: { version: current.version, ...patch },
    })
      .then((ack: Ack) => {
        if (ack.ok) {
          const row = ack.row as FollowupRecord | undefined
          // Re-arm (and report success) only for THIS record's row. api.ts
          // already requires a well-formed row on ok; this guards the row
          // being the wrong one — a bug we would rather surface than store.
          if (!row || typeof row !== 'object' || row.id !== current.id) {
            say('Saved, but the confirmation did not match this record. Reload the screen to see the stored values.', 'followup-queued')
            return
          }
          current = row
          say(ack.noop ? 'Saved. Nothing had changed.' : 'Saved.', 'followup-ok')
          onSaved?.(row as FollowupRow)
          window.dispatchEvent(new CustomEvent('dropby:data-changed'))
          return
        }
        if (ack.status === 409) {
          say('Not saved: this record was changed somewhere else since you opened it. Your values are still here; reload the screen, then re-enter what still applies.', 'followup-error')
        } else if (ack.status >= 400 && ack.status < 500) {
          say(`Not saved: ${ack.error ?? 'rejected'}. Your values are still here.`, 'followup-error')
        } else {
          say('Waiting for signal. It saves when the phone reconnects; your values are kept here.', 'followup-queued')
        }
      })
      .catch(() => {
        // mutate() does not throw in normal operation; if it ever does, the
        // editor must not stay locked and half-flagged.
        say('Not saved: something went wrong. Your values are still here.', 'followup-error')
      })
      .finally(() => {
        saving = false
        fields.disabled = false
        for (const b of [contacted, snooze, clear, save]) b.disabled = false
        emitState()
      })
  })

  function say(text: string, modifier: string): void {
    status.textContent = text
    status.className = `followup-status${modifier ? ` ${modifier}` : ''}`
  }

  form.append(fields, actions, status)
  section.append(form)
  host.append(section)
  emitState()
  return { isDirty, isSaving: () => saving }
}
